import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Switch } from '@/components/forms/Switch';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import { relatedClientsApi } from '@/api/services/relatedClients';
import { RelatedClientsPermissions as P } from './permissions';
import type { JournalAccessByDeviceDto, JournalAccessByIpDto, RelatedLoginPlatformDto } from '@/api/types/relatedClients';

type LoModal = { login: number; connector: number } | null;

function RelatedLoginsCell({ value, map, onOpen }: { value: string; map: RelatedLoginPlatformDto[]; onOpen: (login: number, connector: number) => void }) {
  if (!value) return <span>{value}</span>;
  const items = String(value).split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <>
      {items.map((item, i) => {
        const m = item.match(/^(\d+)\(([^)]*)\)$/);
        const sep = i > 0 ? ', ' : '';
        if (!m) return <span key={i}>{sep}{item}</span>;
        const login = m[1];
        const connectorName = m[2];
        const found = map?.find((x) => String(x.login) === login);
        if (!found) return <span key={i}>{sep}{item}</span>;
        return (
          <span key={i}>
            {sep}
            <a href="#" className="fw-semibold" onClick={(e) => { e.preventDefault(); onOpen(Number(login), found.platformId); }}>
              {login}({connectorName})
            </a>
          </span>
        );
      })}
    </>
  );
}

const currentYear = new Date().getFullYear();

function IpTab({ onOpen }: { onOpen: (login: number, connector: number) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState({ year: currentYear as number | null, type: null as number | null, login: null as number | null, countryId: null as number | null, related: false });
  const [applied, setApplied] = useState(draft);

  const years = useQuery({ queryKey: ['rc', 'years'], queryFn: relatedClientsApi.getYears, staleTime: 5 * 60_000 });
  const types = useQuery({ queryKey: ['rc', 'types'], queryFn: relatedClientsApi.getTypes, staleTime: 5 * 60_000 });
  const countries = useQuery({ queryKey: ['rc', 'countries'], queryFn: relatedClientsApi.getCountries, staleTime: 5 * 60_000 });

  const extra = useMemo(() => ({
    login: applied.login ?? 0,
    related: applied.related,
    year: applied.year ?? currentYear,
    type: applied.type ?? 0,
    countryId: applied.countryId ?? 0,
  }), [applied]);

  const table = useServerDataTable<JournalAccessByIpDto, typeof extra>({ queryKey: ['rc', 'ip'], fetcher: (req) => relatedClientsApi.getDataByIp(req), pageSize: 5, extraParams: extra });

  const warn = (year: number | null) => relatedClientsApi.getConnectorWarning(year).then((r) => { if (r?.message) toast.info(r.message); }).catch(() => {});
  const warnedRef = useRef(false);
  useEffect(() => { if (!warnedRef.current) { warnedRef.current = true; void warn(applied.year ?? null); } /* eslint-disable-next-line */ }, []);

  const fetch = () => { table.setPageIndex(0); setApplied({ ...draft }); void warn(draft.year ?? null); };
  const clear = () => { const d = { year: currentYear as number | null, type: null, login: null, countryId: null, related: false }; setDraft(d); table.setPageIndex(0); setApplied(d); void warn(currentYear); };

  const str = (v: number | null) => (v == null ? '' : String(v));
  return (
    <>
      <FilterBar
        fields={[
          <Select2 key="year" label="Year" allowClear={false} searchable={false} value={str(draft.year)} options={years.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, year: v ? Number(v) : null }))} />,
          <Select2 key="type" label="Type" allowClear placeholder="All" value={str(draft.type)} options={types.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, type: v ? Number(v) : null }))} />,
          <div key="login"><label className="form-label">Login</label><input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} /></div>,
          <Select2 key="country" label="Country" allowClear placeholder="All" value={str(draft.countryId)} options={countries.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, countryId: v ? Number(v) : null }))} />,
        ]}
        card={false}
        modes={<Switch label="Related" checked={draft.related} onChange={(c) => setDraft((f) => ({ ...f, related: c }))} />}
        actions={<FetchClear onFetch={fetch} onClear={clear} busy={table.isFetching} />}
      />
      <div className="border-top mt-2 pt-2">
      <DataGrid
        columns={[
          { key: 'ip', title: 'IP', className: 'text-start', value: (r) => r.ip },
          { key: 'build', title: 'Build', className: 'text-start', value: (r) => r.build },
          { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
          { key: 'relatedLogins', title: 'Related Logins', className: 'text-start', value: (r) => r.relatedLogins, cell: (r) => <RelatedLoginsCell value={r.relatedLogins} map={r.loginPlatformMap} onOpen={onOpen} /> },
          { key: 'type', title: 'Type', className: 'text-start', value: (r) => r.type },
        ] as DataGridColumn<JournalAccessByIpDto>[]}
        data={table.rows}
        rowKey={(r) => r.ip}
        card={false}
        includeExcel
        excelFileName="Related Clients By IP"
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
      </div>
    </>
  );
}

function DeviceTab({ onOpen }: { onOpen: (login: number, connector: number) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState({ yearForDevice: currentYear as number | null, loginForDevice: null as number | null, relatedForDevice: false });
  const [applied, setApplied] = useState(draft);
  const years = useQuery({ queryKey: ['rc', 'years'], queryFn: relatedClientsApi.getYears, staleTime: 5 * 60_000 });

  const extra = useMemo(() => ({
    loginForDevice: applied.loginForDevice ?? 0,
    yearForDevice: applied.yearForDevice ?? currentYear,
    relatedForDevice: applied.relatedForDevice,
  }), [applied]);
  const table = useServerDataTable<JournalAccessByDeviceDto, typeof extra>({ queryKey: ['rc', 'device'], fetcher: (req) => relatedClientsApi.getDataByDevice(req), pageSize: 5, extraParams: extra });

  const warn = (year: number | null) => relatedClientsApi.getConnectorWarning(year).then((r) => { if (r?.message) toast.info(r.message); }).catch(() => {});
  const warnedRef = useRef(false);
  useEffect(() => { if (!warnedRef.current) { warnedRef.current = true; void warn(applied.yearForDevice ?? null); } /* eslint-disable-next-line */ }, []);

  const fetch = () => { table.setPageIndex(0); setApplied({ ...draft }); void warn(draft.yearForDevice ?? null); };
  const clear = () => { const d = { yearForDevice: currentYear as number | null, loginForDevice: null, relatedForDevice: false }; setDraft(d); table.setPageIndex(0); setApplied(d); void warn(currentYear); };
  const str = (v: number | null) => (v == null ? '' : String(v));

  return (
    <>
      <FilterBar
        fields={[
          <Select2 key="year" label="Year" allowClear={false} searchable={false} value={str(draft.yearForDevice)} options={years.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, yearForDevice: v ? Number(v) : null }))} />,
          <div key="login"><label className="form-label">Login</label><input className="form-control" inputMode="numeric" value={draft.loginForDevice ?? ''} onChange={(e) => setDraft((f) => ({ ...f, loginForDevice: e.target.value ? Number(e.target.value) : null }))} /></div>,
        ]}
        card={false}
        modes={<Switch label="Related" checked={draft.relatedForDevice} onChange={(c) => setDraft((f) => ({ ...f, relatedForDevice: c }))} />}
        actions={<FetchClear onFetch={fetch} onClear={clear} busy={table.isFetching} />}
      />
      <div className="border-top mt-2 pt-2">
      <DataGrid
        columns={[
          { key: 'deviceId', title: 'Device Id', className: 'text-start', value: (r) => r.deviceId },
          { key: 'build', title: 'Build', className: 'text-start', value: (r) => r.build },
          { key: 'relatedLogins', title: 'Related Logins', className: 'text-start', value: (r) => r.relatedLogins, cell: (r) => <RelatedLoginsCell value={r.relatedLogins} map={r.loginPlatformMap} onOpen={onOpen} /> },
        ] as DataGridColumn<JournalAccessByDeviceDto>[]}
        data={table.rows}
        rowKey={(r) => r.deviceId}
        card={false}
        includeExcel
        excelFileName="Related Clients By Device"
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
      </div>
    </>
  );
}

function FetchClear({ onFetch, onClear, busy }: { onFetch: () => void; onClear: () => void; busy: boolean }) {
  return (
    <>
      <button type="button" className="btn btn-primary btn-sm" onClick={onFetch} disabled={busy}>
        {busy ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
      </button>
      <button type="button" className="btn btn-link btn-sm" onClick={onClear}>Clear All Filters</button>
    </>
  );
}

export function RelatedClientsPage() {
  const can = usePermission();
  const tabs: TabItem[] = [
    can(P.ipTab) && { key: 'client-ip', title: 'Client IP' },
    can(P.deviceTab) && { key: 'client-device', title: 'Client Device' },
  ].filter(Boolean) as TabItem[];
  const [active, setActive] = useState(tabs[0]?.key ?? 'client-ip');
  const [loModal, setLoModal] = useState<LoModal>(null);
  const onOpen = (login: number, connector: number) => { if (connector) setLoModal({ login, connector }); };

  return (
    <div className="related-clients-page">
      <PageHeader title="Related Clients" breadcrumb={['Home', 'Related Clients']} />
      <div className="card custom-card mb-2">
        <div className="card-header p-0"><Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" /></div>
        <div className="card-body">
          {active === 'client-ip' && <IpTab onOpen={onOpen} />}
          {active === 'client-device' && <DeviceTab onOpen={onOpen} />}
        </div>
      </div>
      {loModal && <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />}
    </div>
  );
}
